import clsx from 'clsx';
import {
  CalendarClock,
  ExternalLink,
  FileText,
  Mail,
  MessageSquare,
  Pause,
  Play,
} from 'lucide-react';

import type { AutomationSummary } from './automation-summary';

/**
 * Action principale d'une automatisation, au même endroit pour tous les types :
 * - Webhook actif → « Exécuter » (orange) : lance tout de suite via la modale de paramètres.
 * - Formulaire / Chat → ouvre l'outil dans un nouvel onglet (bleu).
 * - Planifié / E-mail → pas d'action : indique quand l'automatisation se lance seule.
 * Une automatisation qui n'est pas active n'offre aucune action.
 * L'API revérifie les droits de déclenchement côté serveur.
 */
export function AutomationActionButton({
  automation: a,
  onRun,
  size = 'sm',
}: {
  automation: AutomationSummary;
  onRun: (a: AutomationSummary) => void;
  /** `sm` : listes ; `md` : en-tête de fiche ; `block` : pleine largeur (tuiles). */
  size?: 'sm' | 'md' | 'block';
}) {
  const sizeCls = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-10 px-4 text-sm',
    block: 'min-h-10 w-full px-3 py-2 text-sm !whitespace-normal text-center',
  }[size];
  const base = clsx(
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
    sizeCls,
  );
  const passive = clsx(
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-slate-100 font-medium text-slate-600',
    sizeCls,
  );

  if (a.status !== 'ACTIVE') {
    return (
      <span className={passive}>
        <Pause className="h-3.5 w-3.5" aria-hidden="true" />
        {a.status === 'DRAFT' ? 'En préparation' : 'Indisponible'}
      </span>
    );
  }

  switch (a.triggerType) {
    case 'WEBHOOK':
      return (
        <button
          type="button"
          onClick={() => onRun(a)}
          className={clsx(
            base,
            'bg-accent text-white shadow-sm hover:bg-accent-600 focus-visible:ring-accent/50',
          )}
        >
          <Play className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
          Exécuter
        </button>
      );
    case 'FORM':
      return a.formUrl ? (
        <a
          href={a.formUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(
            base,
            'bg-brand text-white shadow-sm hover:bg-brand-700 focus-visible:ring-brand/50',
          )}
        >
          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
          Remplir le formulaire
          <ExternalLink className="h-3 w-3 opacity-80" aria-hidden="true" />
        </a>
      ) : (
        <span className={passive}>Formulaire non configuré</span>
      );
    case 'CHAT':
      return a.chatUrl ? (
        <a
          href={a.chatUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(
            base,
            'border border-brand-200 bg-white text-brand-600 hover:bg-brand-50 focus-visible:ring-brand/50',
          )}
        >
          <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
          Ouvrir le chat
          <ExternalLink className="h-3 w-3 opacity-80" aria-hidden="true" />
        </a>
      ) : (
        <span className={passive}>Chat non configuré</span>
      );
    case 'SCHEDULED':
      return (
        <span className={passive} title="Se lance automatiquement selon sa planification">
          <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
          {a.scheduleLabel ? `Automatique · ${a.scheduleLabel}` : 'Automatique'}
        </span>
      );
    case 'EMAIL':
      return (
        <span
          className={passive}
          title={a.monitoredEmail ? `Boîte surveillée : ${a.monitoredEmail}` : undefined}
        >
          <Mail className="h-3.5 w-3.5" aria-hidden="true" />À réception d’un e-mail
        </span>
      );
    default:
      return null;
  }
}
